import { useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { adminApi } from '@/lib/adminApi';
import { feedbackApi } from '@/lib/feedbackApi';
import { KIND_LABEL, SEVERITY_HEX, type InteractionRow } from '@/lib/safetyTables';
import { AdminPage, RegistryTable, FeedbackControl, Pill } from '@/admin';

const REGISTRY = 'safety_tables';

const label = (key: string) => key.replace(/_/g, ' ');
const capitalise = (s: string) => s.charAt(0).toUpperCase() + s.slice(1);

function Source({ citation }: { citation?: string | null }) {
  return citation
    ? <span className="text-[12px]">{citation}</span>
    : <span className="text-[12px] text-text-muted">No source recorded</span>;
}

function Sources({ cites }: { cites: string[] }) {
  return cites.length
    ? <span className="text-[12px]">{cites.map((c) => <span key={c} className="block">{c}</span>)}</span>
    : <span className="text-[12px] text-text-muted">No source recorded</span>;
}

function Terms({ items }: { items: string[] }) {
  return items.length
    ? <span className="text-[12px]">{items.join(', ')}</span>
    : <span className="text-[12px] text-text-muted">—</span>;
}

type KeyedRow = { key: string; terms: string[]; extra: string[]; written: string[] };
type NoteRow = { id: string; a: string; b: string; note: string; citation?: string | null };
// `_members` lets a search for a medicine (generic or brand) find the class rows covering it.
type IxRow = InteractionRow & { _id: string; _members: string[] };

// Read-only view of the deterministic safety tables: every interaction row, the notes that
// sit below caution, the medicine classes, and the allergen, condition and food tables.
// Authored in code; this page shows what each row does and the source it cites. Advisors
// can flag a row; nothing is edited here.
export function SafetyTablesPage() {
  const qc = useQueryClient();
  const q = useQuery({ queryKey: ['safety-tables'], queryFn: () => adminApi.safetyTables.list() });
  const fb = useQuery({ queryKey: ['feedback-summary', REGISTRY], queryFn: () => feedbackApi.summary(REGISTRY) });
  const refresh = () => void qc.invalidateQueries({ queryKey: ['feedback-summary', REGISTRY] });
  const [ixSearch, setIxSearch] = useState('');
  const [alSearch, setAlSearch] = useState('');
  const [foodSearch, setFoodSearch] = useState('');
  const d = q.data;
  const citeIndex = new Map((d?.citations ?? []).map((c) => [`${c.table}|${c.key}|${c.term}`, c.citation]));
  const citesFor = (table: string, key: string, terms: string[]) =>
    terms.flatMap((t) => { const c = citeIndex.get(`${table}|${key}|${t}`); return c ? [`${t} — ${c}`] : []; });
  const state = { loading: q.isLoading, error: q.isError };
  const flag = (id: string, itemLabel: string) => (
    <FeedbackControl registry={REGISTRY} itemId={id} itemLabel={itemLabel} openCount={fb.data?.[id]?.open ?? 0} onChanged={refresh} />
  );

  const members = d?.medication_classes.members ?? {};
  const ratified = d?.medication_classes.ratified ?? {};
  const membersOf = (klass: string) => Object.entries(members).filter(([, cs]) => cs.includes(klass)).map(([m]) => m);
  const interactions: IxRow[] = (d?.interactions ?? []).map((r) => ({
    ...r, _id: `${r.a}|${r.b}`, _members: r.b_kind === 'medication_class' ? membersOf(r.b) : [],
  }));
  const cited = interactions.filter((r) => r.citation).length;
  const notes: NoteRow[] = [
    ...(d?.class_notes ?? []).map((n) => ({
      id: `note:${n.a}|${n.b}`, a: n.a, b: `${n.b} (medicine class)`, note: n.note, citation: n.citation,
    })),
    ...(d?.allergen_cautions ?? []).flatMap((c) => c.compounds.map((herb) => ({
      id: `caution:${c.allergen}|${herb}`, a: herb, b: `${label(c.allergen)} (allergy)`,
      note: (c.copy ?? '').replace('{herb}', capitalise(herb)),
      citation: citeIndex.get(`allergen_cautions|${c.allergen}|${herb}`) ?? null,
    }))),
  ];
  const classNames = [...new Set([...Object.keys(ratified), ...Object.values(members).flat()])];
  const classes: KeyedRow[] = classNames.map((name) => ({
    key: name,
    terms: membersOf(name),
    extra: ratified[name] ? [`${ratified[name].by ?? '—'}, ${ratified[name].on ?? '—'}`] : ['not ratified'],
    written: ratified[name]?.scope ? [ratified[name].scope as string] : [],
  }));
  const allergens: KeyedRow[] = Object.entries(d?.allergen_compounds ?? {}).map(([key, terms]) => ({
    key, terms,
    extra: Object.entries((d?.stated_other_source ?? []).filter((s) => s.allergen === key)
      .reduce<Record<string, string[]>>((acc, s) => { const k = s.sources.join(', '); (acc[k] ??= []).push(s.term ?? key); return acc; }, {}))
      .map(([sources, terms]) => `${terms.join(' / ')}: ${sources}`),
    written: d?.allergen_synonyms[key] ?? [],
  }));
  const conditions: KeyedRow[] = Object.entries(d?.condition_contraindications ?? {}).map(([key, terms]) => ({
    key, terms,
    extra: terms.flatMap((t) => (d?.not_the_same_compound[t] ?? []).map((x) => `${t} ≠ ${x}`)),
    written: [],
  }));
  const foods: KeyedRow[] = Object.entries(d?.allergen_foods ?? {}).map(([key, terms]) => ({
    key, terms,
    extra: [...new Set((d?.food_stated_safe ?? []).filter((s) => s.allergen === key).flatMap((s) => s.words))],
    written: Object.entries(d?.condition_food_keys ?? {}).filter(([, k]) => k === key).map(([cond]) => cond),
  }));

  return (
    <AdminPage
      title="Safety tables"
      description="The deterministic checks every plan row passes through: supplement–medicine interactions, the notes below caution, medicine classes, and the allergen, condition and food tables. Read-only (authored in code, changed by PR). Flag a row to raise it."
    >
      {d ? (
        <div className="mb-13 text-body-sm text-text-secondary" data-testid="st-summary">
          {interactions.length} interaction rows · {cited} with a recorded source · {notes.length} notes ·{' '}
          {classes.length} medicine classes · {allergens.length} allergen rows · {conditions.length} condition rows ·{' '}
          {foods.length} food rows
          <div className="mt-1 text-[12px] text-text-muted">
            Interaction rows read from <span className="mono">{d.interactions_source ?? '—'}</span> · medicine classes
            v{String(d.medication_classes.version ?? '?')} from <span className="mono">{d.medication_classes.source ?? '—'}</span>
          </div>
        </div>
      ) : null}

      <section className="sect" data-testid="st-interactions">
        <h2 className="sect-h">Interactions <span>({interactions.length})</span></h2>
        <RegistryTable<IxRow>
          columns={[
            { key: 'severity', header: 'Severity', render: (r) => <Pill label={r.severity} color={SEVERITY_HEX[r.severity]} /> },
            { key: 'a', header: 'Supplement', className: 'font-medium', render: (r) => r.a },
            { key: 'b', header: 'With', render: (r) => <>{r.b}{r.b_kind && r.b_kind !== 'compound' ? <span className="ml-1 text-[11px] text-text-muted">({KIND_LABEL[r.b_kind] ?? r.b_kind})</span> : null}</> },
            { key: 'mechanism', header: 'Why', className: 'max-w-[260px] text-[12px] text-text-secondary', render: (r) => r.mechanism || '—' },
            { key: 'user_line', header: 'What the person sees', className: 'max-w-[300px] text-[12px]', render: (r) => r.user_line || <span className="text-text-muted">Generic wording</span> },
            { key: 'citation', header: 'Source', className: 'max-w-[240px]', render: (r) => <Source citation={r.citation} /> },
          ]}
          rows={interactions}
          getRowKey={(r) => r._id}
          searchKeys={['a', 'b', 'mechanism', 'citation', 'severity', '_members']}
          search={{ value: ixSearch, onChange: setIxSearch, placeholder: 'Search interactions (supplement, medicine or brand, source)…' }}
          state={state}
          emptyText={ixSearch ? `No interaction rows match "${ixSearch}"` : 'No interaction rows.'}
          rowActions={(r) => flag(`interaction:${r._id}`, `${r.a} × ${r.b}`)}
        />
      </section>

      <section className="sect" data-testid="st-notes">
        <h2 className="sect-h">Notes, below caution <span>({notes.length})</span></h2>
        <p className="mb-2 text-body-sm text-text-secondary">Shown on the still-active row with the safety-check badge. Never a hold or a caution.</p>
        <RegistryTable<NoteRow>
          columns={[
            { key: 'a', header: 'Supplement', className: 'font-medium', render: (r) => r.a },
            { key: 'b', header: 'With', render: (r) => r.b },
            { key: 'note', header: 'What the person sees', className: 'max-w-[420px] text-[12px]', render: (r) => r.note },
            { key: 'citation', header: 'Source', className: 'max-w-[240px]', render: (r) => <Source citation={r.citation} /> },
          ]}
          rows={notes} getRowKey={(r) => r.id} state={state} emptyText="No notes."
          rowActions={(r) => flag(r.id, `${r.a} × ${r.b}`)}
        />
      </section>

      <section className="sect" data-testid="st-classes">
        <h2 className="sect-h">Medicine classes <span>({classes.length})</span></h2>
        <RegistryTable<KeyedRow>
          columns={[
            { key: 'key', header: 'Class', className: 'font-medium', render: (r) => label(r.key) },
            { key: 'terms', header: 'Members (generic and brand)', className: 'max-w-[480px]', render: (r) => <Terms items={r.terms} /> },
            { key: 'extra', header: 'Ratified', className: 'whitespace-nowrap text-[12px] text-text-secondary', render: (r) => r.extra.join(' · ') || '—' },
            { key: 'written', header: 'Scope', className: 'max-w-[320px] text-[12px] text-text-secondary', render: (r) => r.written[0] || '—' },
          ]}
          rows={classes} getRowKey={(r) => r.key} state={state}
          rowActions={(r) => flag(`class:${r.key}`, `class ${r.key}`)}
        />
      </section>

      <section className="sect" data-testid="st-allergens">
        <h2 className="sect-h">Allergies → supplements held <span>({allergens.length})</span></h2>
        <RegistryTable<KeyedRow>
          columns={[
            { key: 'key', header: 'Allergy', className: 'font-medium', render: (r) => label(r.key) },
            { key: 'terms', header: 'Supplements held', render: (r) => <Terms items={r.terms} /> },
            { key: 'written', header: 'Also read from', className: 'text-[12px] text-text-secondary', render: (r) => <Terms items={r.written} /> },
            { key: 'extra', header: 'Not held when the label states', className: 'max-w-[280px] text-[12px] text-text-secondary', render: (r) => <Terms items={r.extra} /> },
            { key: 'src', header: 'Source', className: 'max-w-[260px]', render: (r) => <Sources cites={citesFor('allergen_compounds', r.key, r.terms)} /> },
          ]}
          rows={allergens} getRowKey={(r) => r.key} state={state}
          searchKeys={['key', 'terms', 'written']}
          search={{ value: alSearch, onChange: setAlSearch, placeholder: 'Search allergies or supplements…' }}
          rowActions={(r) => flag(`allergen:${r.key}`, `allergy ${r.key}`)}
        />
      </section>

      <section className="sect" data-testid="st-conditions">
        <h2 className="sect-h">Conditions → supplements held <span>({conditions.length})</span></h2>
        <RegistryTable<KeyedRow>
          columns={[
            { key: 'key', header: 'Condition', className: 'font-medium', render: (r) => label(r.key) },
            { key: 'terms', header: 'Supplements held', render: (r) => <Terms items={r.terms} /> },
            { key: 'extra', header: 'Not the same compound', className: 'text-[12px] text-text-secondary', render: (r) => <Terms items={r.extra} /> },
            { key: 'src', header: 'Source', className: 'max-w-[260px]', render: (r) => <Sources cites={citesFor('condition_contraindications', r.key, r.terms)} /> },
          ]}
          rows={conditions} getRowKey={(r) => r.key} state={state}
          rowActions={(r) => flag(`condition:${r.key}`, `condition ${r.key}`)}
        />
      </section>

      <section className="sect" data-testid="st-foods">
        <h2 className="sect-h">Allergies → foods held <span>({foods.length})</span></h2>
        <RegistryTable<KeyedRow>
          columns={[
            { key: 'key', header: 'Allergy', className: 'font-medium', render: (r) => label(r.key) },
            { key: 'terms', header: 'Foods held', className: 'max-w-[440px]', render: (r) => <Terms items={r.terms} /> },
            { key: 'extra', header: 'Not held when stated', className: 'text-[12px] text-text-secondary', render: (r) => <Terms items={r.extra} /> },
            { key: 'written', header: 'Also held for the condition', className: 'text-[12px] text-text-secondary', render: (r) => <Terms items={r.written} /> },
            { key: 'src', header: 'Source', className: 'max-w-[260px]', render: (r) => <Sources cites={citesFor('allergen_foods', r.key, r.terms)} /> },
          ]}
          rows={foods} getRowKey={(r) => r.key} state={state}
          searchKeys={['key', 'terms']}
          search={{ value: foodSearch, onChange: setFoodSearch, placeholder: 'Search allergies or foods…' }}
          rowActions={(r) => flag(`food:${r.key}`, `food allergy ${r.key}`)}
        />
      </section>

      <section className="sect" data-testid="st-nonfood">
        <h2 className="sect-h">Allergies with no food link <span>({d?.non_food_allergies.length ?? 0})</span></h2>
        <p className="text-body-sm text-text-secondary">
          A meal row is reported as checked against these: {(d?.non_food_allergies ?? []).join(', ') || '—'}.
        </p>
      </section>
    </AdminPage>
  );
}
